import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldAlert, Sparkles } from "lucide-react";
import api, { API } from "@/lib/api";
import ResumePreview from "@/components/resume-builder/ResumePreview";

/** PUBLIC, unauthenticated view of a shared resume (see /r/:shareId in App.js) -- powers the
 * "Shareable Resume Link" AI Tool. Always renders the owner's CURRENT saved draft. */
export default function PublicResumePage() {
  const { shareId } = useParams();
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/resume-builder/public/${shareId}`)
      .then(({ data }) => setDraft(data.draft))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="public-resume-loading">
        <div className="w-10 h-10 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !draft) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="public-resume-not-found">
        <div className="glass-panel rounded-2xl p-10 max-w-md w-full text-center">
          <ShieldAlert className="w-12 h-12 text-rose-400 mx-auto mb-3" />
          <h1 className="font-heading text-xl font-semibold text-white">This link is invalid or has been revoked</h1>
          <p className="text-sm text-slate-400 mt-2">Ask the resume owner for a fresh shareable link.</p>
          <Link to="/" className="inline-block mt-6 text-sm text-indigo-400 hover:text-indigo-300">Cloudivo →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-10 px-4" data-testid="public-resume-page">
      <div className="max-w-3xl mx-auto">
        <div className="glass-panel rounded-2xl p-4 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="public-resume-cta-banner">
          <p className="text-xs text-slate-300 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-indigo-400" /> Built with Cloudivo's AI Resume Builder</p>
          <Link to="/resume-builder" data-testid="public-resume-build-your-own-button" className="text-xs font-semibold bg-indigo-500 hover:bg-indigo-400 text-white px-4 py-2 rounded-lg transition-colors">Build your own resume →</Link>
        </div>

        {draft.video_intro_recording_id && (
          <div className="glass-panel rounded-2xl p-4 mb-6" data-testid="public-resume-video-intro">
            <p className="text-xs font-semibold text-white mb-2">Video Introduction</p>
            <video src={`${API}/resume-builder/public/${shareId}/video-intro`} controls className="w-full rounded-lg max-h-96 bg-black" data-testid="public-resume-video-element" />
          </div>
        )}

        <div className="bg-white rounded-2xl overflow-hidden shadow-2xl" data-testid="public-resume-preview">
          <ResumePreview draft={draft} />
        </div>
      </div>
    </div>
  );
}
